import {
  runRenameCanvasWorkflow,
  runShareCanvasWorkflow,
} from "@/action-dialog";
import { restoreErrorMessage, useRestoreCanvas } from "@/api/archived";
import { useDeleteCanvas } from "@/api/canvas";
import { useCheckBulkPermissions } from "@/api/relations";
import { useUserInfo } from "@/api/user";
import { useUsers } from "@/api/users";
import { showMovedToTrashToast } from "@/components/ConfirmTrashDialog/movedToTrashToast";
import { ROUTES } from "@/constants/routes";
import { SelectableItem } from "@/dnd/SelectableItem";
import { route } from "@/utils/route";
import {
  type Canvas,
  type CheckBulkPermissionsCheck,
} from "@cohesive/api-client";
import { type CanvasOwner } from "@cohesive/ui-components";
import { useCallback, useMemo } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { CanvasCardContainer } from "../components/CanvasCardContainer";
import { handleWorkspaceItemActivate } from "../workspaceItemActivate";
import { useWorkspaceSelectionOptional } from "./useWorkspaceSelection";

export function useCanvasCardActions({
  canvases,
  selectable = true,
  draggable = selectable,
}: {
  canvases: Canvas[] | undefined;
  /** When true, plain clicks participate in the workspace-selection
   *  model (cmd/shift-click builds a multi-selection, marquee drags
   *  sweep rows, etc.). Turn off on surfaces like the Home "Recents"
   *  row where selection affordances aren't useful. */
  selectable?: boolean;
  /** When true, cards are wrapped in `SelectableItem` so dnd-kit
   *  registers them as drag sources. Defaults to `selectable` (drag
   *  and select usually travel together), but callers can enable
   *  drag on a non-selectable surface — e.g. Home, where we want
   *  drag-to-move-canvas but not selection UI. */
  draggable?: boolean;
}) {
  const navigate = useNavigate();
  const { data: userInfo } = useUserInfo();
  const { data: users = [] } = useUsers();
  const currentUserId = userInfo?.user?.id;
  const selectionCtx = useWorkspaceSelectionOptional();
  const selection = selectable ? selectionCtx : null;
  /** Drag wraps still need selection ctx (for `selectedIds` /
   *  `selectOnly` snapshot) — `draggable` can be true even when
   *  `selectable` is false, so we read from `selectionCtx` directly
   *  here instead of the gated `selection` alias above. */
  const dragSelection = draggable ? selectionCtx : null;

  const usersMap = useMemo(() => {
    const map = new Map<string, CanvasOwner>();
    users.forEach((user) => {
      map.set(user.id, {
        id: user.id,
        name: user.name,
        picture_url: user.picture_url,
      });
    });
    return map;
  }, [users]);

  const permissionChecks = useMemo<CheckBulkPermissionsCheck[]>(() => {
    if (!canvases || canvases.length === 0) return [];
    return canvases.flatMap((canvas) => [
      {
        resource_object_type: "canvas" as const,
        resource_object_id: canvas.id,
        permission: "can_delete" as const,
      },
      {
        resource_object_type: "canvas" as const,
        resource_object_id: canvas.id,
        permission: "can_manage_assignments" as const,
      },
    ]);
  }, [canvases]);

  const { data: permissions } = useCheckBulkPermissions({
    checks: permissionChecks,
  });

  const deleteCanvas = useDeleteCanvas({
    onError: () => toast.error("Failed to delete canvas"),
  });
  const restoreCanvas = useRestoreCanvas({
    onError: (err) =>
      toast.error(restoreErrorMessage(err, "Failed to restore Canvas")),
  });

  const handleRename = useCallback((canvas: Canvas) => {
    window.setTimeout(() => void runRenameCanvasWorkflow({ canvas }), 0);
  }, []);

  const handleMove = useCallback((canvas: Canvas) => {
    // Submenu-only — the destination picker is the interaction; the old
    // MoveCanvasDialog is retired.
    void canvas;
  }, []);

  const handleOpen = useCallback(
    (canvas: Canvas) => {
      navigate(route(ROUTES.CANVAS.DETAIL, { canvasId: canvas.id }));
    },
    [navigate],
  );

  const handleShare = useCallback((canvas: Canvas) => {
    window.setTimeout(() => void runShareCanvasWorkflow({ canvas }), 0);
  }, []);

  // One-click move-to-trash with Undo in the toast — no confirmation dialog,
  // since trashed canvases stay restorable.
  const handleDelete = useCallback(
    (canvas: Canvas) => {
      deleteCanvas.mutate(canvas.id, {
        onSuccess: () =>
          showMovedToTrashToast(() => restoreCanvas.mutate(canvas.id)),
      });
    },
    [deleteCanvas, restoreCanvas],
  );

  const canShare = useCallback(
    (canvasId: string) =>
      permissions?.canvas?.[canvasId]?.can_manage_assignments ?? false,
    [permissions],
  );

  const canDelete = useCallback(
    (canvasId: string) => permissions?.canvas?.[canvasId]?.can_delete ?? false,
    [permissions],
  );

  /** Owner for the per-card owner pill. We intentionally hide it
   *  when the canvas belongs to the current user — the pill is there
   *  to disambiguate "someone else made this" at a glance, so
   *  labeling your own canvases with your own avatar is pure noise. */
  const getOwner = useCallback(
    (canvas: Canvas) =>
      canvas.user_id !== currentUserId
        ? usersMap.get(canvas.user_id)
        : undefined,
    [currentUserId, usersMap],
  );

  /** Always-resolved owner for list-view attribution columns. Unlike
   *  `getOwner`, this returns a value for the current user's own
   *  canvases too (as "You") so the "Created by" column is never
   *  mysteriously blank. Returns `undefined` only when we can't
   *  resolve the user at all (e.g. member outside the workspace). */
  const getCreator = useCallback(
    (canvas: Canvas): CanvasOwner | undefined => {
      if (canvas.user_id === currentUserId && userInfo?.user) {
        return {
          id: userInfo.user.id,
          name: "You",
          picture_url: userInfo.user.picture_url,
        };
      }
      return usersMap.get(canvas.user_id);
    },
    [currentUserId, userInfo, usersMap],
  );

  const renderCanvasCard = useCallback(
    (canvas: Canvas, _index: number) => {
      const selId = `canvas:${canvas.id}`;
      const isSelected = selection?.isSelected(selId) ?? false;

      const card = (
        <CanvasCardContainer
          key={canvas.id}
          canvas={canvas}
          canShare={canShare(canvas.id)}
          canDelete={canDelete(canvas.id)}
          isSelected={isSelected}
          owner={getOwner(canvas)}
          /** When the surface participates in workspace selection
           *  (workspace detail / Shared with me) we mirror the file
           *  and folder card pattern: single click selects, double
           *  click opens on desktop. On touch-primary surfaces the
           *  second tap on an already-selected card opens instead
           *  (dblclick is unreliable on touch). On link-like surfaces
           *  with no selection context (Home / Recents) plain click
           *  opens directly. The menu's "Open" item routes through the
           *  registry handler so it always navigates regardless of how
           *  `onClick` is wired. */
          onClick={
            selection
              ? (e) =>
                  handleWorkspaceItemActivate({
                    isSelected,
                    e,
                    onOpen: () => handleOpen(canvas),
                    onSelect: () => selection.cardClick(selId, e),
                  })
              : () => handleOpen(canvas)
          }
          onDoubleClick={() => handleOpen(canvas)}
        />
      );

      if (!dragSelection) return card;

      return (
        <SelectableItem
          key={canvas.id}
          id={selId}
          kind="canvas"
          selectedIds={dragSelection.selectedIds}
          selectOnly={dragSelection.selectOnly}
        >
          {card}
        </SelectableItem>
      );
    },
    [selection, dragSelection, handleOpen, canShare, canDelete, getOwner],
  );

  const dialogs = null;

  // Memoised so consumers can safely include `actions` in their own
  // useMemo / effect dependency arrays without invalidating the cache
  // every render (see WorkspaceDetailPage.canvasItems).
  const actions = useMemo(
    () => ({
      open: handleOpen,
      rename: handleRename,
      move: handleMove,
      share: handleShare,
      delete: handleDelete,
      canShare,
      canDelete,
      getOwner,
      getCreator,
    }),
    [
      handleOpen,
      handleRename,
      handleMove,
      handleShare,
      handleDelete,
      canShare,
      canDelete,
      getOwner,
      getCreator,
    ],
  );

  return { renderCanvasCard, dialogs, actions };
}
